import React, { useEffect, useState } from 'react';
import { useModal } from '../context/ModalContext';

const initialEligibleForm = {
  name: '',
  company: '',
  phone: '',
  email: '',
  industry: '',
  turnover: '',
  funding_amount: '',
  message: ''
};

const initialNotEligibleForm = {
  name: '',
  company: '',
  phone: '',
  email: '',
  turnover: ''
};

const FundingSathiModal = () => {
  const { isOpen, closeModal } = useModal();
  const [step, setStep] = useState('eligibility');
  const [eligibleForm, setEligibleForm] = useState(initialEligibleForm);
  const [notEligibleForm, setNotEligibleForm] = useState(initialNotEligibleForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('eligibility');
      setEligibleForm(initialEligibleForm);
      setNotEligibleForm(initialNotEligibleForm);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleEligibility = (isEligible) => {
    setStep(isEligible ? 'eligible' : 'not_eligible');
  };

  const handleEligibleChange = (event) => {
    const { name, value } = event.target;
    setEligibleForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNotEligibleChange = (event) => {
    const { name, value } = event.target;
    setNotEligibleForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event, type) => {
    event.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...(type === 'eligible' ? eligibleForm : notEligibleForm),
      _lead_type: type
    };

    const subject = encodeURIComponent(
      type === 'eligible'
        ? `Funding Sathi Inquiry - Eligible Business`
        : `Funding Sathi Inquiry - Not Eligible Business`
    );

    const body = encodeURIComponent(
      Object.entries(payload)
        .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value || 'Not provided'}`)
        .join('\n')
    );

    const mailtoLink = `mailto:info@fundingsathi.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;

    setTimeout(() => {
      setIsSubmitting(false);
      setStep('thanks');
    }, 600);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/60 p-4"
      onClick={closeModal}
    >
      <div
        className="relative w-full max-w-xl max-h-[calc(100vh-4rem)] overflow-y-auto rounded-3xl bg-[#F7F7F3] p-8 shadow-[0_10px_40px_rgba(0,0,0,0.2)]"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute right-4 top-4 text-3xl font-semibold text-[#0D0D0D] transition hover:text-[#800020]"
          aria-label="Close modal"
        >
          &times;
        </button>

        {step === 'eligibility' && (
          <div>
            <h2 className="text-3xl font-semibold text-[#541828]">Let's get started</h2>
            <p className="fs-sub text-base leading-relaxed text-[#0D0D0D] mt-4 mb-6">
              Funding Sathi currently works with companies maintaining an <b>annual turnover of ₹100 Cr or above</b>.
            </p>
            <p className="fs-question text-lg font-semibold text-[#333333] mb-4">Does your company meet this criteria?</p>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={() => handleEligibility(true)}
                className="fs-btn fs-btn-primary rounded-xl bg-[#541828] px-5 py-3 text-sm font-semibold text-[#F7F7F3] transition hover:opacity-90"
              >
                Yes, ₹100 Cr+
              </button>
              <button
                type="button"
                onClick={() => handleEligibility(false)}
                className="fs-btn fs-btn-secondary rounded-xl bg-[#0D0D0D] px-5 py-3 text-sm font-semibold text-[#F7F7F3] transition hover:opacity-90"
              >
                No / Not yet
              </button>
            </div>
          </div>
        )}

        {step === 'eligible' && (
          <div>
            <h2 className="text-3xl font-semibold text-[#541828]">Tell us about your business</h2>
            <form onSubmit={(event) => handleSubmit(event, 'eligible')} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={eligibleForm.name}
                  onChange={handleEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Company Name *</label>
                <input
                  type="text"
                  name="company"
                  value={eligibleForm.company}
                  onChange={handleEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={eligibleForm.phone}
                  onChange={handleEligibleChange}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={eligibleForm.email}
                  onChange={handleEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Industry / Sector</label>
                <input
                  type="text"
                  name="industry"
                  value={eligibleForm.industry}
                  onChange={handleEligibleChange}
                  placeholder="e.g. Manufacturing, Trading, Pharma"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Annual Turnover (₹ Cr) *</label>
                <input
                  type="number"
                  name="turnover"
                  value={eligibleForm.turnover}
                  onChange={handleEligibleChange}
                  required
                  min="100"
                  placeholder="e.g. 120"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Funding Amount Required (₹)</label>
                <input
                  type="text"
                  name="funding_amount"
                  value={eligibleForm.funding_amount}
                  onChange={handleEligibleChange}
                  placeholder="e.g. 5 Cr"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Brief Requirement</label>
                <textarea
                  name="message"
                  value={eligibleForm.message}
                  onChange={handleEligibleChange}
                  rows={3}
                  placeholder="Tell us briefly what kind of funding/support you need"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="fs-btn fs-btn-primary w-full rounded-xl bg-[#541828] px-5 py-3 text-sm font-semibold text-[#F7F7F3] transition hover:opacity-90 disabled:opacity-70"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        )}

        {step === 'not_eligible' && (
          <div>
            <h2 className="text-3xl font-semibold text-[#541828]">Thanks for your interest!</h2>
            <p className="text-base leading-relaxed text-[#0D0D0D] mt-4 mb-6">
              Funding Sathi's current focus is on companies with ₹100 Cr+ annual turnover.
              Your details will still be saved — our team may reach out as our services expand,
              or to guide you to the right resources.
            </p>
            <form onSubmit={(event) => handleSubmit(event, 'not_eligible')} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={notEligibleForm.name}
                  onChange={handleNotEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Company Name *</label>
                <input
                  type="text"
                  name="company"
                  value={notEligibleForm.company}
                  onChange={handleNotEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={notEligibleForm.phone}
                  onChange={handleNotEligibleChange}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={notEligibleForm.email}
                  onChange={handleNotEligibleChange}
                  required
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D0D0D] mb-2">Current Annual Turnover (₹ Cr)</label>
                <input
                  type="number"
                  name="turnover"
                  value={notEligibleForm.turnover}
                  onChange={handleNotEligibleChange}
                  placeholder="e.g. 40"
                  className="w-full rounded-xl border border-[#D1D5DB] bg-white px-4 py-3 text-sm outline-none focus:border-[#541828] focus:ring-2 focus:ring-[#541828]/20"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="fs-btn fs-btn-secondary w-full rounded-xl bg-[#0D0D0D] px-5 py-3 text-sm font-semibold text-[#F7F7F3] transition hover:opacity-90 disabled:opacity-70"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Anyway'}
              </button>
            </form>
          </div>
        )}

        {step === 'thanks' && (
          <div className="text-center">
            <h2 className="text-3xl font-semibold text-[#541828]">Thank you!</h2>
            <p className="text-base leading-relaxed text-[#0D0D0D] mt-4 mb-6">
              We've received your details. Our team will get in touch with you shortly.
            </p>
            <button
              type="button"
              onClick={closeModal}
              className="fs-btn fs-btn-primary rounded-xl bg-[#541828] px-6 py-3 text-sm font-semibold text-[#F7F7F3] transition hover:opacity-90"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundingSathiModal;
